// src/App.js
import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar/Sidebar";
import ScenarioTabs from "./components/ScenarioTabs/ScenarioTabs";
import WizardAddPerson from "./components/Person/WizardAddPerson";
import WizardAddEntity from "./components/Entity/WizardAddEntity";
import ConfirmModal from "./components/Common/ConfirmModal";
import { useConfirm } from "./hooks/useConfirm";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import LoginPage from "./auth/LoginPage";
import "./App.css";
import apiService from "./api";

// Main app content (shown when authenticated)
const AppContent = () => {
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Wizard state - managed at App level for full-page display
  const [isPersonWizardOpen, setIsPersonWizardOpen] = useState(false);
  const [isEntityWizardOpen, setIsEntityWizardOpen] = useState(false);
  const [onWizardSave, setOnWizardSave] = useState(null);
  const [onEntityWizardSave, setOnEntityWizardSave] = useState(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  const calculateLanes = useCallback((boxes) => {
    const lanes = [];
    boxes.forEach(box => {
      const laneIndex = box.lane_position;
      if (!lanes[laneIndex]) {
        lanes[laneIndex] = { id: laneIndex, level: laneIndex };
      }
    });
    return lanes.filter(lane => lane); // Remove empty lanes
  }, []);

  const processScenario = useCallback((scenario, index) => {
    console.log(`[App] Processing scenario ${scenario.id}:`, JSON.stringify(scenario, null, 2));

    // Calculate lanes from boxes, but preserve default lanes if no boxes exist
    const calculatedLanes = calculateLanes([...scenario.individuals, ...scenario.entities]);
    const lanes = calculatedLanes.length > 0 ? calculatedLanes : (scenario.lanes || [
      { id: 0, level: 0 },
      { id: 1, level: 1 }
    ]);
    console.log("[App] Calculated lanes:", lanes);

    // Build unified items[] array - single source of truth
    const items = [
      ...scenario.individuals.map(individual => ({
        // Type identifier
        type: 'individual',
        // Standard ID format
        id: individual.id || individual.individual_id,
        uniqueBoxId: `individual-${individual.id || individual.individual_id}`,
        // Rendering fields
        content: 'Individu',
        headerColor: "#FF6F61",
        laneId: individual.lane_position,
        boxPosition: individual.box_position,
        // ALL other fields
        ...individual
      })),
      ...scenario.entities.map(entity => ({
        // Type identifier
        type: 'entity',
        // Standard ID format
        id: entity.id || entity.entity_id,
        uniqueBoxId: `entity-${entity.id || entity.entity_id}`,
        // Rendering fields
        content: 'Entity',
        headerColor: "#FFD700",
        laneId: entity.lane_position,
        boxPosition: entity.box_position,
        // ALL other fields
        ...entity
      }))
    ];

    // boxes is now an alias for items (backward compatibility)
    const boxes = items;
    console.log("[App] Processed items/boxes:", JSON.stringify(boxes, null, 2));

    const relations = scenario.shareholdings ? scenario.shareholdings.map(sh => {
      console.log("[App] Processing shareholding:", JSON.stringify(sh, null, 2));
      const startBox = boxes.find(b => 
        (b.id === sh.individual_scenario_owner_id && b.content === 'Individu') || 
        (b.id === sh.entity_scenario_owner_id && b.content === 'Entity')
      );
      const endBox = boxes.find(b => 
        b.id === sh.entity_scenario_owned_id && b.content === 'Entity'
      );
      
      console.log("[App] Found startBox:", JSON.stringify(startBox, null, 2));
      console.log("[App] Found endBox:", JSON.stringify(endBox, null, 2));

      if (!startBox || !endBox) {
        console.warn(`[App] Could not find boxes for shareholding:`, JSON.stringify(sh, null, 2));
        return null;
      }

      const relation = {
        id: `${startBox.uniqueBoxId}-${endBox.uniqueBoxId}`,
        dbId: sh.id,
        startBox: {
          laneId: startBox.laneId,
          boxId: startBox.uniqueBoxId
        },
        endBox: {
          laneId: endBox.laneId,
          boxId: endBox.uniqueBoxId
        },
        percentage: sh.percentage,
        role: sh.role,
        isManager: sh.is_manager || sh.isManager
      };
      console.log("[App] Created relation:", JSON.stringify(relation, null, 2));
      return relation;
    }).filter(Boolean) : [];
    console.log("[App] Processed relations:", JSON.stringify(relations, null, 2));

    return {
      ...scenario,
      displayOrder: index + 1,
      lanes,
      items,      // New unified array (single source of truth)
      boxes,      // Alias for items (backward compatibility)
      relations,
      selectedBoxes: scenario.selectedBoxes || []
    };
  }, [calculateLanes]);

  const createDefaultScenario = useCallback(() => {
    const newId = Date.now();
    return {
      id: newId,
      name: "New Scenario",
      description: "",
      lanes: [
        { id: 0, level: 0 },
        { id: 1, level: 1 }
      ],
      items: [],          // New unified array (single source of truth)
      boxes: [],          // Alias for items (backward compatibility)
      individuals: [],    // Kept for backend compatibility
      entities: [],       // Kept for backend compatibility
      shareholdings: []
    };
  }, []);

  const updateDisplayOrder = useCallback((scenarios) => {
    return scenarios
      .sort((a, b) => a.id - b.id)
      .map((scenario, index) => ({
        ...scenario,
        displayOrder: index + 1
      }));
  }, []);

  const loadScenarios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedScenarios = await apiService.fetchScenariosWithDetails();
      console.log("[App] Fetched scenarios:", JSON.stringify(fetchedScenarios, null, 2));

      if (!fetchedScenarios || fetchedScenarios.length === 0) {
        const defaultScenario = createDefaultScenario();
        const processedScenario = processScenario(defaultScenario, 0);
        setScenarios([processedScenario]);
        setActiveScenarioId(processedScenario.id);
      } else {
        const scenariosWithStructure = fetchedScenarios
          .sort((a, b) => a.id - b.id)
          .map((scenario, index) => processScenario(scenario, index));

        console.log("[App] Final scenarios structure:", JSON.stringify(scenariosWithStructure, null, 2));
        setScenarios(scenariosWithStructure);
        setActiveScenarioId(scenariosWithStructure[0].id);
      }
    } catch (error) {
      console.error("[App] Error loading scenarios:", error);
      setError("Failed to load scenarios. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  }, [createDefaultScenario, processScenario]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const addScenario = () => {
    const newScenario = createDefaultScenario();
    const processedNewScenario = processScenario(newScenario, scenarios.length);
    setScenarios(prevScenarios => {
      const updatedScenarios = [...prevScenarios, processedNewScenario];
      return updateDisplayOrder(updatedScenarios);
    });
    setActiveScenarioId(processedNewScenario.id);
  };

  const removeScenario = async (idToRemove) => {
    try {
      await apiService.deleteScenario(idToRemove);
      setScenarios(prevScenarios => {
        const updatedScenarios = prevScenarios.filter(scenario => scenario.id !== idToRemove);
        const orderedScenarios = updateDisplayOrder(updatedScenarios);

        // Update active scenario if we're removing the currently active one
        if (activeScenarioId === idToRemove) {
          const newActiveId = orderedScenarios.length > 0 ? orderedScenarios[0].id : null;
          // Use setTimeout to avoid state update during render
          setTimeout(() => setActiveScenarioId(newActiveId), 0);
        }

        return orderedScenarios;
      });
    } catch (error) {
      console.error("[App] Error removing scenario:", error);
      setError("Failed to remove scenario. Please try again.");
    }
  };

  const updateScenarioState = (id, updater) => {
    setScenarios(prevScenarios => {
      const updatedScenarios = prevScenarios.map(scenario =>
        scenario.id === id ? updater(scenario) : scenario
      );
      return updateDisplayOrder(updatedScenarios);
    });
  };

  const addLane = () => {
    if (!activeScenarioId) {
      showConfirm({
        title: 'Aucun scénario',
        message: 'Veuillez créer ou sélectionner un scénario d\'abord.',
        confirmText: 'OK',
        cancelText: '',
        type: 'info',
        onConfirm: () => {}
      });
      return;
    }

    const activeScenario = scenarios.find(s => s.id === activeScenarioId);
    if (activeScenario && activeScenario.lanes.length >= 4) {
      showConfirm({
        title: 'Limite atteinte',
        message: 'La limite de 4 voies a été atteinte.',
        confirmText: 'OK',
        cancelText: '',
        type: 'info',
        onConfirm: () => {}
      });
      return;
    }

    updateScenarioState(activeScenarioId, (scenario) => {
      const newLaneId = scenario.lanes.length > 0 ? Math.max(...scenario.lanes.map(lane => lane.id)) + 1 : 0;
      return {
        ...scenario,
        lanes: [...scenario.lanes, { id: newLaneId, level: scenario.lanes.length }],
      };
    });
  };

  const saveScenario = async () => {
    const activeScenario = scenarios.find(s => s.id === activeScenarioId);
    if (!activeScenario) return;

    setIsSaving(true);
    setSaveSuccess(false);
    setError(null);

    try {
      // Process deleted relations - clean up related data
      const deletedRelations = activeScenario._deletedRelations || [];
      for (const relation of deletedRelations) {
        if (!relation) continue;
        const { dbId, startBox, endBox, role, isManager } = relation;

        // Delete shareholding from DB (force=true since we handle cleanup)
        if (dbId) {
          try {
            await apiService.deleteShareholding(dbId, true);
            console.log("[App] Deleted shareholding:", dbId);
          } catch (err) {
            console.error("[App] Error deleting shareholding:", err);
          }
        }

        // Delete linked income from individual
        if (isManager && startBox) {
          try {
            const individualBox = activeScenario.boxes?.find(b => b.uniqueBoxId === startBox.boxId);
            const individualId = individualBox?.id || individualBox?.individual_id;
            if (individualId) {
              const incomes = await apiService.fetchIncomes(individualId);
              const linkedIncome = incomes.find(inc =>
                inc.shareholding_id === dbId ||
                inc.shareholdingId === dbId ||
                (inc.income_type === 'REMUNERATION_DIRIGEANT' && inc.is_auto_generated)
              );
              if (linkedIncome) {
                await apiService.deleteIncome(linkedIncome.id);
                console.log("[App] Deleted linked income:", linkedIncome.id);
              }
            }
          } catch (err) {
            console.error("[App] Error deleting linked income:", err);
          }
        }

        // Clear entity manager's individualId
        if (isManager && role && endBox) {
          try {
            const entityBox = activeScenario.boxes?.find(b => b.uniqueBoxId === endBox.boxId);
            const entityId = entityBox?.id || entityBox?.entity_id;
            if (entityId) {
              const entity = await apiService.getEntity(entityId);
              const managers = entity.managers || [];
              const managerRole = role.toLowerCase();
              const updatedManagers = managers.map(m => {
                if (m.role?.toLowerCase() === managerRole) {
                  // Only clear individualId - preserve entryDate and salaryStartDate
                  return { ...m, individualId: null };
                }
                return m;
              });
              await apiService.updateEntity(entityId, { managers: updatedManagers });
              console.log("[App] Cleared entity manager link:", entityId, role);
            }
          } catch (err) {
            console.error("[App] Error clearing entity manager:", err);
          }
        }
      }

      let savedScenario;
      // IDs from Date.now() are timestamps (> 1 trillion), database IDs are small numbers
      const isNewScenario = activeScenario.id > 1000000000000;

      if (isNewScenario) {
        console.log("[App] Creating new scenario with full data...", activeScenario);

        // Extract individuals and entities from items[] (single source of truth)
        const itemsArray = activeScenario.items || activeScenario.boxes || [];
        const individualsFromItems = itemsArray
          .filter(item => item.type === 'individual' || item.content === 'Individu')
          .map(item => ({
            template_id: item.template_id,
            lane_position: item.laneId ?? item.lane_position,
            box_position: item.boxPosition ?? item.box_position,
            first_name: item.first_name,
            last_name: item.last_name,
            marital_status: item.marital_status,
            children_count: item.children_count,
            single_parent: item.single_parent,
            employment_type: item.employment_type,
            fiscal_residence: item.fiscal_residence
          }));

        const entitiesFromItems = itemsArray
          .filter(item => item.type === 'entity' || item.content === 'Entity')
          .map(item => ({
            template_id: item.template_id,
            lane_position: item.laneId ?? item.lane_position,
            box_position: item.boxPosition ?? item.box_position,
            entity_name: item.entity_name,
            entity_type: item.entity_type,
            tax_regime: item.tax_regime,
            activity_type: item.activity_type,
            fiscal_year_end: item.fiscal_year_end,
            share_capital: item.share_capital,
            fiscal_residence: item.fiscal_residence,
            creation_date: item.creation_date,
            managers: item.managers,
            has_acre: item.has_acre,
            has_cga: item.has_cga,
            tva_regime: item.tva_regime,
            dividend_percent: item.dividend_percent,
            has_employees: item.has_employees,
            employees_count: item.employees_count,
            employees_avg_salary: item.employees_avg_salary,
            employees_months: item.employees_months,
            employee_profiles: item.employee_profiles,
            charges_mode: item.charges_mode,
            charges_data: item.charges_data,
            fiscal_params: item.fiscal_params,
            advanced_options: item.advanced_options,
            forecast_start_date: item.forecast_start_date || item.forecastStartDate,
            forecast_end_date: item.forecast_end_date || item.forecastEndDate,
            exercice_data: item.exercice_data
          }));

        savedScenario = await apiService.createScenario({
          name: activeScenario.name,
          description: activeScenario.description,
          startDate: activeScenario.startDate,
          endDate: activeScenario.endDate,
          startingCapital: activeScenario.startingCapital,
          individuals: individualsFromItems,
          entities: entitiesFromItems,
          shareholdings: activeScenario.shareholdings
        });

        // Set primaryIndividualId using the first created individual
        if (savedScenario.primaryIndividualId) {
          console.log("[App] Setting primaryIndividualId for new scenario:", savedScenario.primaryIndividualId);
          await apiService.updateScenario(savedScenario.id, {
            primaryIndividualId: savedScenario.primaryIndividualId
          });
        }
      } else {
        console.log("[App] Updating existing scenario...");
        // Only send metadata - NOT entities/individuals (they're managed separately)
        savedScenario = await apiService.updateScenario(activeScenario.id, {
          name: activeScenario.name,
          description: activeScenario.description,
          startDate: activeScenario.startDate,
          endDate: activeScenario.endDate,
          startingCapital: activeScenario.startingCapital,
          primaryIndividualId: activeScenario.primaryIndividualId || activeScenario.primary_individual_id
        });
      }
      console.log("[App] Scenario saved successfully", savedScenario);

      // Merge API response with existing scenario data (keep boxes, lanes, relations)
      // Clear deleted relations tracking
      updateScenarioState(activeScenarioId, (prev) => ({
        ...prev,
        id: savedScenario.id,
        name: savedScenario.name,
        description: savedScenario.description,
        created_at: savedScenario.created_at,
        updated_at: savedScenario.updated_at,
        _deletedRelations: [],
        _hasUnsavedChanges: false
      }));

      if (isNewScenario && savedScenario.id) {
        setActiveScenarioId(savedScenario.id);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[App] Error saving scenario:", error);
      setError("Failed to save the scenario. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Open person wizard (called from Sidebar)
  const openPersonWizard = (saveCallback) => {
    setOnWizardSave(() => saveCallback);
    setIsPersonWizardOpen(true);
  };

  const closePersonWizard = () => {
    setIsPersonWizardOpen(false);
    setOnWizardSave(null);
  };

  // Save and close wizard (final submit)
  const handlePersonWizardSave = async (data) => {
    if (onWizardSave) {
      await onWizardSave(data);
    }
    closePersonWizard();
  };

  // Save draft without closing wizard
  const handlePersonWizardSaveDraft = async (data) => {
    if (onWizardSave) {
      await onWizardSave(data);
    }
    // Don't close - just save
  };

  // Open entity wizard (called from Sidebar)
  const openEntityWizard = (saveCallback) => {
    setOnEntityWizardSave(() => saveCallback);
    setIsEntityWizardOpen(true);
  };

  const closeEntityWizard = () => {
    setIsEntityWizardOpen(false);
    setOnEntityWizardSave(null);
  };

  // Save entity and close wizard
  const handleEntityWizardSave = async (data) => {
    if (onEntityWizardSave) {
      await onEntityWizardSave(data);
    }
    closeEntityWizard();
  };

  return (
    <div className="app">
      <Sidebar
        onOpenPersonWizard={openPersonWizard}
        onOpenEntityWizard={openEntityWizard}
      />
      <div className="main-content">
        {isPersonWizardOpen ? (
          <WizardAddPerson
            onSave={handlePersonWizardSave}
            onSaveDraft={handlePersonWizardSaveDraft}
            onClose={closePersonWizard}
          />
        ) : isEntityWizardOpen ? (
          <WizardAddEntity
            onSave={handleEntityWizardSave}
            onClose={closeEntityWizard}
          />
        ) : (
          <ScenarioTabs
            scenarios={scenarios}
            activeScenarioId={activeScenarioId}
            setActiveScenarioId={setActiveScenarioId}
            addScenario={addScenario}
            removeScenario={removeScenario}
            updateScenario={updateScenarioState}
            addLane={addLane}
            onSave={saveScenario}
            isSaving={isSaving}
            saveSuccess={saveSuccess}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

// App wrapper with auth
const App = () => {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
};

// Gate component: shows login or app based on auth state
const AuthGate = () => {
  const { isAuthenticated, loading, needsSetup } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0F172A', color: '#F59E0B',
        fontSize: '18px', fontWeight: 600
      }}>
        Chargement...
      </div>
    );
  }

  if (!isAuthenticated || needsSetup) {
    return <LoginPage />;
  }

  return <AppContent />;
};

export default App;